import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../../api"; // adjust path to your axios instance
import { refreshStoredDmsPdf } from "../utils/dmsToPdf";   // Go-live parity (2026-09-24)

const ApprovalPage = ({ action }) => {
    const { token } = useParams();
    const [status, setStatus] = useState("loading"); // loading | success | error | already
    const [message, setMessage] = useState("");

    useEffect(() => {
        const process = async () => {
            try {
                // Decode token: "dmsId:approverId:action"
                const decoded = atob(decodeURIComponent(token));
                const [dmsId, approverId] = decoded.split(":");

                if (!dmsId || !approverId) {
                    setStatus("error");
                    setMessage("Invalid or malformed token.");
                    return;
                }

                const endpoint = action === "approve"
                    ? `/dms/${dmsId}/approve`
                    : `/dms/${dmsId}/deny`;

                await api.post(endpoint, { approverId: Number(approverId) });
                // Go-live parity (2026-09-24): keep the stored PDF in step with
                // the new status (fire-and-forget).
                refreshStoredDmsPdf(dmsId, api);

                setStatus("success");
                setMessage(action === "approve"
                    ? "Document has been approved successfully."
                    : "Document has been denied and marked for revision.");
            } catch (err) {
                const msg = err?.response?.data?.message || "";
                if (err?.response?.status === 404) {
                    setStatus("already");
                    setMessage("This action has already been processed or the record was not found.");
                } else {
                    setStatus("error");
                    setMessage(msg || "Something went wrong. Please try again or contact support.");
                }
            }
        };

        process();
    }, [token, action]);

    const config = {
        loading: { icon: "ri-loader-4-line", color: "#534AB7", bg: "#EEEDFE", title: "Processing..." },
        success: { icon: action === "approve" ? "ri-checkbox-circle-line" : "ri-forbid-line", color: action === "approve" ? "#198754" : "#dc3545", bg: action === "approve" ? "#d1e7dd" : "#f8d7da", title: action === "approve" ? "Approved!" : "Denied" },
        already: { icon: "ri-information-line", color: "#856404", bg: "#fff3cd", title: "Already Processed" },
        error: { icon: "ri-close-circle-line", color: "#dc3545", bg: "#f8d7da", title: "Error" },
    }[status];

    return (
        <div style={styles.page}>
            <style>{`@keyframes ap-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
            <div style={styles.card}>
                {/* Logo */}
                <div style={styles.logoWrap}>
                    <div style={styles.logoText}>S.S. White Technologies</div>
                </div>

                <div style={{ ...styles.iconBox, background: config.bg }}>
                    <span style={{ fontSize: "48px", color: config.color, lineHeight: 1 }}><i className={config.icon} style={status === "loading" ? { animation: "ap-spin .9s linear infinite", display: "inline-block" } : undefined} /></span>
                </div>

                <h2 style={{ ...styles.title, color: config.color }}>{config.title}</h2>
                <p style={styles.msg}>{message || "Please wait while we process your request..."}</p>

                {status !== "loading" && (
                    <a href="/dms" style={{ ...styles.btn, background: config.color }}>
                        Go to Document Management
                    </a>
                )}
            </div>
        </div>
    );
};

const styles = {
    page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f4f4f8" },
    card: { background: "#fff", borderRadius: "16px", padding: "40px 48px", boxShadow: "0 4px 24px rgba(0,0,0,0.10)", textAlign: "center", maxWidth: "440px", width: "100%" },
    logoWrap: { marginBottom: "24px" },
    logoText: { fontSize: "15px", fontWeight: 700, color: "#534AB7", letterSpacing: "0.5px" },
    iconBox: { width: "88px", height: "88px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" },
    title: { fontSize: "22px", fontWeight: 700, margin: "0 0 10px" },
    msg: { fontSize: "14px", color: "#555", margin: "0 0 28px", lineHeight: "1.6" },
    btn: { display: "inline-block", padding: "10px 24px", borderRadius: "8px", color: "#fff", textDecoration: "none", fontWeight: 600, fontSize: "14px" },
};

export default ApprovalPage;